import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { DistributionPoint } from '@/lib/types/division-profile';
import { cn } from '@/lib/utils';

interface DistributionChartProps {
  data: DistributionPoint[];
  label?: string;
  height?: number;
  valueFormatter?: (value: number) => string;
  /** Default sorts bars by value desc; use `asGiven` for ordered buckets (e.g. armor tiers). */
  sortBy?: 'valueDesc' | 'asGiven';
  yAxisWidth?: number;
}

/** Theme token — same family as other UI accents (not raw HSL). */
const BAR_FILL = 'var(--primary)';
const BAR_FILL_OPACITY = 0.85;

/** Cursor band over chart — muted wash (theme vars are oklch, not `hsl(var(...))`). */
const CURSOR_FILL = 'color-mix(in oklch, var(--muted) 45%, transparent)';

type TooltipPayloadItem = {
  value?: number | string;
  payload?: DistributionPoint;
};

function DistributionTooltipBody({
  active,
  payload,
  valueFormatter,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  valueFormatter: (value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0];
  const point = row?.payload;
  const raw = row?.value;
  if (point === undefined || raw === undefined) return null;
  const value = typeof raw === 'number' ? raw : Number(raw);

  return (
    <div
      className={cn(
        'max-w-[14rem] rounded-lg border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-xl'
      )}
    >
      <p className="font-medium leading-tight text-foreground">{point.name}</p>
      <p className="mt-0.5 tabular-nums text-muted-foreground">
        {valueFormatter(value)}
      </p>
    </div>
  );
}

function truncateName(name: string, maxLen = 14): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen - 1) + '\u2026';
}

export function DistributionChart({
  data,
  label,
  height = 140,
  valueFormatter,
  sortBy = 'valueDesc',
  yAxisWidth = 100,
}: DistributionChartProps) {
  if (data.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic py-2">
        No data available
      </p>
    );
  }

  const fmt = valueFormatter ?? ((v: number) => String(v));
  const sorted =
    sortBy === 'asGiven'
      ? [...data]
      : [...data].sort((a, b) => b.value - a.value);

  return (
    <div>
      {label && (
        <p className="text-xs text-muted-foreground mb-1 font-medium">
          {label}
        </p>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={sorted}
          layout="vertical"
          margin={{ top: 0, right: 4, bottom: 0, left: 0 }}
          barCategoryGap="20%"
        >
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            width={yAxisWidth}
            tick={{
              fontSize: 11,
              fill: 'var(--muted-foreground)',
            }}
            tickFormatter={(v: string) => truncateName(v)}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: CURSOR_FILL }}
            content={
              <DistributionTooltipBody valueFormatter={fmt} />
            }
          />
          <Bar dataKey="value" radius={[0, 3, 3, 0]} maxBarSize={16}>
            {sorted.map((_, i) => (
              <Cell
                key={i}
                fill={BAR_FILL}
                fillOpacity={BAR_FILL_OPACITY}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
